import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CANCELLATION_REASON_PRESETS } from '../schedule/CancelDateModal';
import { NO_SHOW_REASON_LABELS } from '../../services/attendanceReasons';

// Use the shared canonical list so the dropdown options here match the
// Attendance page's detail editor and Weekly Attendance's inline picker.
const NO_SHOW_PRESETS = NO_SHOW_REASON_LABELS;

const POPUP_WIDTH = 320;

export type AttendanceActionKind = 'tap-in' | 'tap-out' | 'no-show' | 'dismiss';
export type AttendanceTiming = 'early' | 'late';

interface Props {
  /** Bounding rect of the button that opened the popup; null = closed. */
  anchorRect: DOMRect | null;
  kind: AttendanceActionKind;
  timing?: AttendanceTiming;
  childName: string;
  /** Pre-fill values used when the popup is editing an existing record
   *  (e.g. tweaking the reason on an already-saved no-show). Both default
   *  to empty for the original "creating a new record" use cases. */
  defaultReason?: string;
  defaultComment?: string;
  /** tap-in / tap-out only — the editable actual time (HH:MM). Defaults to
   *  the current clock time so the previous "record now" behaviour is the
   *  starting point, but the user can correct it to the real arrival /
   *  departure moment instead of it being silently assumed to be now. */
  defaultTime?: string;
  /** The scheduled arrival / departure (HH:MM); selecting "On time" snaps the
   *  editable time to this value. */
  scheduledTime?: string;
  /** Optional bounds (HH:MM) for the editable time — e.g. a sign-out can't
   *  precede the recorded arrival (the DB enforces departure >= arrival). */
  minTime?: string;
  maxTime?: string;
  /** For tap-in / tap-out the third arg is the resolved actual time (HH:MM);
   *  for no-show / dismiss it is null. */
  onConfirm: (comment: string | null, reason: string | null, time: string | null) => void;
  onCancel: () => void;
}

// Theme palette — mirrors the timing colours on the NOW card buttons so the
// popup feels visually wired to whichever button opened it.
const THEME = {
  'on-time': { accent: '#7cb342', tintBg: '#eef7e2', icon: 'fas fa-check' },
  early:     { accent: '#e8a92e', tintBg: '#fdf6e6', icon: 'fas fa-hourglass-start' },
  late:      { accent: '#ea7c30', tintBg: '#fdf1e6', icon: 'fas fa-hourglass-half' },
  noshow:    { accent: '#b8302f', tintBg: '#fbecec', icon: 'fas fa-user-slash' },
  // "Cancel session (one-off)" — burnt-amber to echo the .btn-warning used
  // by the Weekly Schedule's CancelDateModal so both surfaces feel like
  // the same action.
  dismiss:   { accent: '#a86a00', tintBg: '#fdf3df', icon: 'fas fa-ban' },
};

const AttendanceConfirmModal: React.FC<Props> = ({
  anchorRect, kind, timing, childName,
  defaultReason, defaultComment,
  defaultTime, scheduledTime, minTime, maxTime,
  onConfirm, onCancel,
}) => {
  const [comment, setComment] = useState(defaultComment ?? '');
  const [reason, setReason] = useState(defaultReason ?? '');
  // The editable actual time for a tap-in / tap-out (HH:MM). Seeded from the
  // current clock time so committing without touching it matches the old
  // "record now" behaviour, but the user can correct it.
  const [time, setTime] = useState(defaultTime ?? '');
  // User can override an early/late classification to "on time" — handles the
  // case where they actually signed the child in/out at the right moment but
  // forgot to click promptly.
  const [overrideOnTime, setOverrideOnTime] = useState(false);

  // Sync internal state to the latest defaults each time the popup opens
  // OR closes. The previous "reset only on close" version meant that if
  // the parent's defaults changed *after* mount (e.g. a row's stored
  // no-show reason was edited elsewhere), the dropdown could still show
  // the stale value the next time the user clicked Edit. Triggering on
  // anchorRect alone (and not on defaults) keeps user typing intact while
  // the popup is open — defaults aren't expected to change mid-edit since
  // the busy-gate pauses the silent refresh while a modal is up.
  useEffect(() => {
    setComment(defaultComment ?? '');
    setReason(defaultReason ?? '');
    setTime(defaultTime ?? '');
    setOverrideOnTime(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRect]);

  // Vertical placement: prefer below the trigger, flip above when the popup
  // would run off the bottom of the viewport (small screens / low button),
  // clamp on-screen as a fallback. Measured after render so the real height
  // is used; useLayoutEffect avoids a visible jump. Kept above the early
  // return below so the hook order stays stable.
  const dialogRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(0);

  useLayoutEffect(() => {
    if (!anchorRect) return;
    const h = dialogRef.current?.offsetHeight ?? 0;
    const margin = 8;
    const below = anchorRect.bottom + margin;
    const above = anchorRect.top - h - margin;
    let next: number;
    if (below + h <= window.innerHeight - margin) {
      next = below;                                              // fits below
    } else if (above >= margin) {
      next = above;                                              // flip above
    } else {
      next = Math.max(margin, window.innerHeight - h - margin);  // clamp on-screen
    }
    setTop(next);
  }, [anchorRect, kind, reason, overrideOnTime, time]);

  if (!anchorRect) return null;

  // Override to on-time is only valid when the scheduled moment has already
  // passed. That maps to timing === 'late' (now > scheduled). For 'early'
  // the scheduled moment is still in the future, so on-time can't apply.
  const canOverrideOnTime = timing === 'late';

  // Effective timing reflects the override toggle (clamped to a valid option).
  const effectiveTiming: AttendanceTiming | 'on-time' =
    overrideOnTime && canOverrideOnTime ? 'on-time' : (timing ?? 'early');

  const themeKey: keyof typeof THEME =
    kind === 'no-show' ? 'noshow'
      : kind === 'dismiss' ? 'dismiss'
        : effectiveTiming;
  const theme = THEME[themeKey];

  const timingLabel = (t: AttendanceTiming | 'on-time') =>
    t === 'on-time' ? 'On time' : t === 'early' ? 'Early' : 'Late';

  const title =
    kind === 'no-show'
      ? `Mark ${childName} as no-show`
      : kind === 'dismiss'
        ? `Cancel session for ${childName} (one-off)`
        : kind === 'tap-in'
          ? `Sign in (${timingLabel(effectiveTiming)})`
          : `Sign out (${timingLabel(effectiveTiming)})`;

  const confirmLabel =
    kind === 'no-show' ? 'Mark as no-show'
      : kind === 'dismiss' ? 'Cancel session'
        : kind === 'tap-in' ? 'Confirm sign in'
          : 'Confirm sign out';

  // tap-in / tap-out carry an editable actual time; the other kinds don't.
  const hasTime = kind === 'tap-in' || kind === 'tap-out';
  const timeBelowMin = hasTime && !!minTime && !!time && time < minTime;
  const timeAboveMax = hasTime && !!maxTime && !!time && time > maxTime;
  const timeInvalid = hasTime && (!time || timeBelowMin || timeAboveMax);

  // Both reason-picker kinds need a preset selected; "Cancel session" with
  // preset === 'Other' additionally requires the freetext field. Same rule
  // the WeeklySchedule's CancelDateModal applies, so the two flows feel
  // identical.
  const trimmedComment = comment.trim();
  const confirmDisabled =
    (kind === 'no-show' && !reason) ||
    (kind === 'dismiss' && (!reason || (reason === 'Other' && !trimmedComment))) ||
    timeInvalid;

  const handleConfirm = () => {
    if (confirmDisabled) return;
    const finalComment = trimmedComment || null;
    const finalReason =
      kind === 'no-show' || kind === 'dismiss' ? (reason || null) : null;
    onConfirm(finalComment, finalReason, hasTime ? (time || null) : null);
  };

  const arrivalOrDeparture = kind === 'tap-in' ? 'arrival' : 'departure';
  const placeholder =
    kind === 'no-show'
      ? 'Additional notes (optional)'
      : kind === 'dismiss'
        ? reason === 'Other'
          ? 'Describe the reason (required)'
          : 'Additional notes (optional)'
        : overrideOnTime
          ? 'Optional note'
          : timing === 'early'
            ? `Note about the early ${arrivalOrDeparture} (optional)`
            : `Note about the late ${arrivalOrDeparture} (optional)`;

  // Horizontal: center on the trigger, clamped 8px inside the viewport.
  // (Vertical `top` is computed above via useLayoutEffect.)
  const centerX = anchorRect.left + anchorRect.width / 2;
  const left = Math.min(
    Math.max(8, centerX - POPUP_WIDTH / 2),
    window.innerWidth - POPUP_WIDTH - 8,
  );

  const labelStyle: React.CSSProperties = {
    fontSize: '0.72rem',
    color: '#5a6a58',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: 4,
  };

  return createPortal(
    <>
      {/* Solid backdrop catches outside clicks (visually transparent so the
          page is still visible — but layered above content for clean clicks). */}
      <div
        onMouseDown={onCancel}
        style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'transparent',
        }}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="att-confirm-title"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top, left,
          width: POPUP_WIDTH,
          zIndex: 9999,
          background: '#ffffff',
          border: `1px solid ${theme.accent}`,
          borderTop: `4px solid ${theme.accent}`,
          borderRadius: 10,
          boxShadow: '0 10px 28px -6px rgba(0,0,0,0.22), 0 4px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        {/* Header strip — accent-tinted, with the action's icon. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            background: theme.tintBg,
            borderBottom: `1px solid ${theme.accent}33`,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: theme.accent,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.8rem',
              flexShrink: 0,
            }}
          >
            <i className={theme.icon} />
          </span>
          <h6
            id="att-confirm-title"
            style={{
              fontWeight: 600,
              margin: 0,
              fontSize: '0.92rem',
              color: '#2a3528',
            }}
          >
            {title}
          </h6>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 16px' }}>
          {kind !== 'no-show' && timing && canOverrideOnTime && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Actually was</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['on-time', timing] as const).map(opt => {
                  const isActive = effectiveTiming === opt;
                  const optTheme = THEME[opt];
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        const toOnTime = opt === 'on-time';
                        setOverrideOnTime(toOnTime);
                        // Snap the editable time: scheduled when "on time",
                        // back to the clock default when re-marked late.
                        setTime(toOnTime ? (scheduledTime ?? '') : (defaultTime ?? ''));
                      }}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        background: isActive ? optTheme.accent : '#fff',
                        color: isActive ? '#fff' : '#3a4538',
                        border: `1px solid ${isActive ? optTheme.accent : 'rgba(0,0,0,0.16)'}`,
                        borderRadius: 6,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'background 140ms ease, border-color 140ms ease',
                      }}
                    >
                      {timingLabel(opt)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {hasTime && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>
                {kind === 'tap-in' ? 'Arrival time' : 'Departure time'}
              </label>
              <input
                type="time"
                value={time}
                min={minTime}
                max={maxTime}
                disabled={overrideOnTime}
                onChange={(e) => setTime(e.target.value)}
                className="form-control form-control-sm"
                autoFocus
                required
                style={overrideOnTime ? { background: '#f4f6f2', opacity: 0.85 } : undefined}
              />
              {(timeBelowMin || timeAboveMax) && (
                <div style={{ fontSize: '0.74rem', color: '#b8302f', marginTop: 6 }}>
                  {timeBelowMin
                    ? `Must be at or after the sign-in time (${minTime}).`
                    : `Must be at or before the sign-out time (${maxTime}).`}
                </div>
              )}
            </div>
          )}

          {(kind === 'no-show' || kind === 'dismiss') && (() => {
            const presets = kind === 'no-show'
              ? NO_SHOW_PRESETS
              : CANCELLATION_REASON_PRESETS;
            return (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>
                  Reason <span style={{ color: theme.accent }}>*</span>
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="form-select form-select-sm"
                  autoFocus
                  required
                  style={{
                    borderColor: reason ? `${theme.accent}66` : undefined,
                  }}
                >
                  <option value="">Select reason…</option>
                  {presets.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            );
          })()}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Comment</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="form-control form-control-sm"
              rows={3}
              placeholder={placeholder}
              style={{ resize: 'vertical' }}
              maxLength={500}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '7px 14px',
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.16)',
                borderRadius: 6,
                color: '#4a5b48',
                fontWeight: 500,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirmDisabled}
              style={{
                padding: '7px 16px',
                background: theme.accent,
                border: 'none',
                borderRadius: 6,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: confirmDisabled ? 'not-allowed' : 'pointer',
                opacity: confirmDisabled ? 0.5 : 1,
                boxShadow: confirmDisabled ? 'none' : '0 1px 2px rgba(0,0,0,0.12)',
              }}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default AttendanceConfirmModal;
